import {
    filter,
    find,
    get,
    groupBy,
    map,
    some,
    isEmpty,
    range,
    sortBy,
    keyBy,
    uniqBy
} from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { Graph } from 'app/lib/utils/graph';
import { createDeepEqualitySelector } from 'app/store/utils';
import { GROUP_NODE } from 'app/lib/constants';
import { selectProjects } from 'app/store/projects';
import { parseFilePaths, Status } from './utils.js';

export const selectIsCreateDialogOpen = get('migration.isCreateDialogOpen');

export const selectIsMigrateDialogOpen = get('migration.isMigrateDialogOpen');

export const selectIsCreatingMigration = get('migration.isCreatingMigration');

export const selectCreateError = get('migration.createError');

export const selectSelectedMigrationId = get('migration.selectedMigrationId');

export const selectSelectedSheetId = get('migration.selectedSheetId');

export const selectMigrations = get('migration.migrations');

export const selectIsMigrationFetched = get('migration.isMigrationFetched');

export const selectMigrationForUid = createSelector(
    (_, props) => props.uid,
    selectMigrations,
    (uid, migrations) => (migrations ?? []).find((migration) => migration.uid === uid)
);

const selectMigrationSheets = get('migration.migrationSheets');

export const selectState = get('migration.state');

export const selectProgress = get('migration.progress');

export const selectProjectUid = get('migration.projectUid');

export const selectExpandedGroups = get('migration.expandedGroups');

export const selectSelectedMigration = createSelector(
    selectSelectedMigrationId,
    selectMigrations,
    (uid, migrations) => find({ uid }, migrations)
);

const selectSelectedMigrationMetadata = createSelector(
    selectSelectedMigration,
    (migration) => migration?.feMetadata
);

export const selectSelectedMigrationSheets = createSelector(
    selectSelectedMigrationId,
    selectMigrationSheets,
    (uid, sheets) => sheets[uid] ?? []
);

export const selectSelectedMigrationIdMappingsBySheetId = createSelector(
    selectSelectedMigrationMetadata,
    (metadata) => groupBy('workbookId', filter('workbookId', metadata?.idMappings ?? []))
);

function extractTargetProjects(migrations, projects) {
    const projectIds = map('uid', projects);
    const projectsById = keyBy('uid', projects);

    return sortBy(
        'name',
        migrations
            .map((migration) => migration.spaceUid)
            .filter((id) => projectIds.includes(id))
            .map((id) => projectsById[id])
    );
}

export const selectInventorySheetList = createSelector(
    [selectSelectedMigrationSheets, selectSelectedMigrationIdMappingsBySheetId, selectProjects],
    (sheets, migrations, projects) =>
        sheets.map(({ id, name, type, ...rest }) => ({
            id,
            name,
            type,
            sheetId: rest.sheetId,
            workbookId: rest.workbookId,
            details: JSON.stringify({ id, ...rest }, null, 4),
            targetProjects: extractTargetProjects(
                uniqBy('spaceUid', migrations[rest.workbookId]),
                projects
            )
        }))
);

export const selectGroups = createSelector(
    selectSelectedMigrationSheets,
    selectExpandedGroups,
    (sheets, expandedGroups) => {
        const groupMap = {};
        return Object.values(
            sheets.reduce((acc, item) => {
                if (['ExportJob', 'ImportJob', 'DataLink'].includes(item.type)) {
                    return acc;
                }
                const workbookId = item.workbookId;
                if (!acc[workbookId]) {
                    acc[workbookId] = {
                        _type: GROUP_NODE,
                        id: workbookId,
                        name: `${item.workbookName}.wbk`,
                        workbookId,
                        children: [],
                        sheetId: item.sheetId,
                        isExpanded: expandedGroups.includes(workbookId)
                    };
                }
                acc[workbookId].children.push(item.id);

                return acc;
            }, groupMap)
        );
    }
);

const selectColumns = createSelector(
    selectSelectedSheetId,
    selectSelectedMigrationSheets,
    (sheetId, sheets) => {
        const sheet = sheets[sheetId];

        if (sheet?.type === 'ExportJob') {
            const sourceSheet = sheets[sheet.sources[0]];

            return sheet.columns.map((column) => {
                const sourceColumn = sourceSheet.columns.find(
                    ({ name }) => name === column.originalName
                );

                return { ...column, type: sourceColumn.type };
            });
        }

        return sheet?.columns;
    }
);

export const selectSelectedSheet = createSelector(
    selectSelectedSheetId,
    selectSelectedMigrationSheets,
    selectColumns,
    (sheetId, sheets, columns) => {
        const sheet = sheets[sheetId];

        return isEmpty(columns) ? sheet : { ...sheet, columns };
    }
);

export const selectSheetForId = createSelector(
    (_, props) => props.id,
    selectSelectedMigrationSheets,
    (id, sheets) => find({ id }, sheets)
);

export const selectGroupForId = createSelector(
    (_, props) => props.id,
    selectGroups,
    (groupId, groups) => groups.find(({ id }) => id === groupId)
);

export const selectGraph = createSelector(selectSelectedMigrationSheets, (sheets) => {
    const edges = [];

    sheets.forEach((sheet) => {
        if (sheet.sources) {
            edges.push(...sheet.sources.map((id) => ({ from: id, to: sheet.id })));
        }
    });

    return new Graph(
        sheets.map((sheet) => sheet.id),
        edges
    );
});

const selectPropCompressed = (_, props) => props?.compressed ?? false;

export const selectGroupedDependencyGraph = createDeepEqualitySelector(
    selectSelectedMigrationSheets,
    selectGraph,
    selectGroups,
    selectPropCompressed,
    (sheets, graph, groups, isCompressed) => {
        if (isEmpty(graph.nodes)) {
            return graph;
        }

        if (isCompressed) {
            const exports = sheets
                .filter((sheet) => sheet.type === 'ExportJob')
                .map((sheet) => sheet.id);

            return graph.subgraph(...exports);
        }

        const collapsedGroups = groups.filter((group) => !group.isExpanded);
        return graph.groupAll(collapsedGroups);
    }
);

export const selectSearchData = createSelector(selectSelectedMigrationSheets, (sheets) =>
    sheets.map(({ id, name }) => ({
        entityId: id,
        name
    }))
);

export const selectSourceSheets = createSelector(
    selectGraph,
    selectSelectedSheetId,
    selectSelectedMigrationSheets,
    (graph, selectedSheetId, sheets) => {
        const upstreamIds = graph.getUpstreamNodes(selectedSheetId);

        return sheets.filter(({ id }) => upstreamIds.includes(id));
    }
);

export const selectUpstreamSheetsIds = createSelector(selectSourceSheets, (sheets) =>
    sheets.map(({ id }) => id)
);

export const selectDownstreamSheetIds = createSelector(
    selectGraph,
    selectSelectedSheetId,
    selectSelectedMigrationSheets,
    (graph, selectedSheetId, sheets) => {
        const downstreamIds = graph.getDownstreamNodes(selectedSheetId);

        return sheets.filter(({ id }) => downstreamIds.includes(id)).map(({ id }) => id);
    }
);

export const selectSelectedSheetMappings = createSelector(
    selectSelectedSheet,
    selectProjects,
    selectSelectedMigrationIdMappingsBySheetId,
    (sheet, projects, mappings) => {
        if (isEmpty(sheet)) {
            return [];
        }

        return extractTargetProjects(uniqBy('spaceUid', mappings[sheet.workbookId]), projects);
    }
);

export const selectIsSheetMigrated = createSelector(
    selectSheetForId,
    selectSelectedMigrationMetadata,
    (sheet, metadata) => {
        if (!sheet?.workbookId) {
            return false;
        }

        return some(
            (mapping) =>
                sheet.sheetId === mapping.sheetId && sheet.workbookId === mapping.workbookId,
            metadata.idMappings ?? []
        );
    }
);

export const selectIsLoadingMigrationFiles = get('migration.isLoadingMigrationFiles');

export const selectMigrationFilesTreeStructure = createSelector(
    selectSelectedMigrationSheets,
    (sheets) => parseFilePaths(sheets)
);

export const selectHasExpandedGroups = createSelector(
    selectExpandedGroups,
    (expandedGroups) => !isEmpty(expandedGroups)
);

export const selectHasCollapsedGroups = createSelector(
    selectExpandedGroups,
    selectGroups,
    (expandedGroups, allGroups) => expandedGroups.length !== allGroups.length
);

export const selectProgressNodes = createSelector(
    selectProgress,
    selectSourceSheets,
    (progress, nodes) => {
        const nodesCount =
            nodes.filter(({ type }) => type !== 'ImportJob' && type !== 'DataLink').length + 2;
        let data = progress;
        if (nodesCount > progress.length) {
            const pending = range(0, nodesCount - progress.length).map((i) => ({
                status: i === 0 ? Status.STARTED : Status.PENDING
            }));
            data = [...progress, ...pending];
        }

        return data.map((data, i) => ({ ...data, id: i }));
    }
);
